// Diagnostics — shipped in v0.3.4 to surface the in-app DiagLog ring
// buffer so we can debug FCM push-registration issues without needing
// adb. Only useful inside the Android wrapper (the JS bridge methods
// live on VultureUpdates); on a plain browser the page renders a
// "diagnostics only available in the Android app" stub.
//
// Three pieces:
//   1. pushStateInfo() snapshot — synchronous, fast. Shows deviceId,
//      whether an FCM token was ever cached locally, whether Auth0
//      credentials are stored, and the server URL the wrapper points
//      at. Helps tell "FCM SDK never delivered" apart from "registered
//      locally but server never got the RPC".
//   2. Re-register push button — clears the local skip-when-unchanged
//      guard and re-launches ensureRegisteredFromAppStart so we can
//      force a fresh attempt. Result lands in DiagLog asynchronously.
//   3. DiagLog snapshot — last ~250 lines from the ring buffer the
//      Kotlin side writes to. Auto-refreshes every 2s so the user
//      sees fresh "FcmService:" lines arrive after tapping the
//      button. Has a Copy button to paste back to support.
//
// All bridge calls are feature-detected: if VultureUpdates is missing
// or one of the optional methods is undefined (older wrapper), we
// render a friendly fallback so the page doesn't blow up.

import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../../lib/rpc";

interface PushState {
  deviceId?: string;
  lastRegisteredTokenLen?: number;
  auth0StoredCredentials?: boolean;
  serverUrl?: string;
  error?: string;
}

function readPushState(): PushState | null {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const bridge = (window as any).VultureUpdates;
  if (!bridge || typeof bridge.pushStateInfo !== "function") return null;
  try { return JSON.parse(bridge.pushStateInfo()); }
  catch (err) { return { error: (err as Error)?.message || "parse failed" }; }
}

function readDiagSnapshot(): string | null {
  const bridge = (window as any).VultureUpdates;
  if (!bridge || typeof bridge.diagSnapshot !== "function") return null;
  try { return bridge.diagSnapshot(); }
  catch (err) { return `(snapshot failed: ${(err as Error)?.message || "err"})`; }
}

function reRegisterPush(): { ok: boolean; error?: string } {
  const bridge = (window as any).VultureUpdates;
  if (!bridge || typeof bridge.rePushRegister !== "function") {
    return { ok: false, error: "bridge missing" };
  }
  try { return JSON.parse(bridge.rePushRegister()); }
  catch (err) { return { ok: false, error: (err as Error)?.message || "parse failed" }; }
}

interface DiagnosticsProps { notify: (msg: string) => void; }

export function SettingsDiagnostics({ notify }: DiagnosticsProps) {
  const [pushState, setPushState] = useState<PushState | null>(readPushState());
  const [log, setLog] = useState<string | null>(readDiagSnapshot());
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-refresh both panels every 2s while the page is open. The
  // DiagLog ring buffer mutates on its own as Kotlin async work
  // completes (FCM token fetch, RPC retry, etc.) — without polling
  // the user would have to leave + reopen the page to see updates.
  useEffect(() => {
    const tick = () => {
      setPushState(readPushState());
      setLog(readDiagSnapshot());
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => clearInterval(id);
  }, []);

  const onRetry = () => {
    setBusy(true);
    const r = reRegisterPush();
    if (r.ok) {
      notify("Re-registering — give it a few seconds and watch the log.");
    } else {
      notify(`Retry failed: ${r.error || "unknown"}`);
    }
    // Pull a fresh state immediately + then back off; the 2s poller
    // keeps the page in sync after.
    setPushState(readPushState());
    setTimeout(() => { setLog(readDiagSnapshot()); setBusy(false); }, 1500);
  };

  const onUpload = async () => {
    const snapshot = readDiagSnapshot();
    if (!snapshot) { notify("No log to upload (wrapper bridge missing)"); return; }
    const state = readPushState();
    const deviceId = state?.deviceId;
    if (!deviceId || deviceId === "(unset)") {
      notify("No device id yet — open the app once after login first");
      return;
    }
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const versionName = (() => {
      try {
        const bridge = (window as any).VultureUpdates;
        if (!bridge?.versionInfo) return null;
        const info = JSON.parse(bridge.versionInfo() || "{}");
        return info.versionName || null;
      } catch { return null; }
    })();
    setUploading(true);
    try {
      await api.diagUploadDeviceLog({ deviceId, versionName, snapshot });
      notify("Uploaded — admins can view it in Admin → App logs");
    } catch (err: unknown) {
      notify(`Upload failed: ${(err as Error)?.message || "unknown"}`);
    } finally {
      setUploading(false);
    }
  };

  const onCopy = async () => {
    const text = log || "(no log)";
    try {
      await navigator.clipboard.writeText(text);
      notify("Copied to clipboard");
    } catch {
      // Fallback for very old WebViews — select the textarea so the user
      // can long-press copy.
      logRef.current?.focus();
      logRef.current?.select();
      notify("Long-press to copy");
    }
  };

  // No bridge at all → plain browser. Render a stub instead of a broken page.
  if (pushState === null && log === null) {
    return (
      <section className="settings-section">
        <h3 className="settings-section-title">Diagnostics</h3>
        <p className="settings-section-help">
          The diagnostics page is only available inside the Android app
          (v0.3.4 or later). Open Settings → Diagnostics there to see
          the FCM registration state + log.
        </p>
      </section>
    );
  }

  return (
    <section className="settings-section">
      <h3 className="settings-section-title">Diagnostics</h3>
      <p className="settings-section-help">
        Internal state of the Android wrapper's push registration. The
        log auto-refreshes every 2 seconds.
      </p>

      <div className="diag-state-grid">
        <div className="diag-state-row">
          <span className="diag-state-label">Device ID</span>
          <span className="diag-state-value">{pushState?.deviceId || "—"}</span>
        </div>
        <div className="diag-state-row">
          <span className="diag-state-label">FCM token cached</span>
          <span className="diag-state-value">
            {pushState?.lastRegisteredTokenLen
              ? `yes (${pushState.lastRegisteredTokenLen} chars)`
              : "no"}
          </span>
        </div>
        <div className="diag-state-row">
          <span className="diag-state-label">Auth0 credentials</span>
          <span className="diag-state-value">
            {pushState?.auth0StoredCredentials ? "stored" : "missing"}
          </span>
        </div>
        <div className="diag-state-row">
          <span className="diag-state-label">Server</span>
          <span className="diag-state-value">{pushState?.serverUrl || "—"}</span>
        </div>
      </div>

      <div className="diag-actions">
        <button type="button" className="settings-btn" onClick={onRetry} disabled={busy}>
          {busy ? "Re-registering…" : "Re-register push"}
        </button>
        <button type="button" className="settings-btn" onClick={onUpload} disabled={uploading}>
          {uploading ? "Uploading…" : "Upload to server"}
        </button>
        <button type="button" className="settings-btn" onClick={onCopy}>
          Copy log
        </button>
      </div>

      <textarea
        ref={logRef}
        className="diag-log"
        readOnly
        spellcheck={false}
        value={log || "(no log lines yet)"}
      />
    </section>
  );
}
